// src/pages/FlashcardPage.jsx
import React, { useState, useContext, useEffect } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { ThemeContext } from '../context/ThemeContext';
import flashcardService from '../services/flashcardservice';
import notesService from '../services/notesService';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';

const FlashcardPage = () => {
  const { theme } = useContext(ThemeContext);
  const [flashcards, setFlashcards] = useState([]);
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sourceText, setSourceText] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState('');
  const [stats, setStats] = useState({
    totalCards: 0,
    masteredCards: 0,
    dueForReview: 0,
    masteryPercentage: 0
  });

  // Clusters for export and flashcard filtering
  const [exportCluster, setExportCluster] = useState('all');
  const [availableClusters, setAvailableClusters] = useState([]);

  // Clusters for flashcard filtering and generation
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('all');

  // Fetch flashcards on component mount
  useEffect(() => {
    fetchFlashcards();
    fetchStats();
    fetchClusters();
  }, []);

  // Fetch notes when generator is shown
  useEffect(() => {
    if (showGenerator) {
      fetchNotes();
    }
  }, [showGenerator]);

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      const response = await flashcardService.getAllFlashcards();
      setFlashcards(response.data.flashcards || []);
    } catch (error) {
      toast.error('Failed to fetch flashcards');
      console.error('Error fetching flashcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await flashcardService.getStats();
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await notesService.getAllNotes();
      setNotes(response.data.notes || []);
    } catch (error) {
      toast.error('Failed to fetch notes');
      console.error('Error fetching notes:', error);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await notesService.getClusters();
      const clusterList = response.data?.clusters || [];
      setClusters(clusterList);
      // Extract cluster names for export dropdown
      const clusterNames = clusterList.map(c => c._id);
      setAvailableClusters(clusterNames);
    } catch (error) {
      console.error('Error fetching clusters:', error);
    }
  };

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentCard((prev) => (prev + 1) % flashcards.length);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setCurrentCard((prev) => (prev - 1 + flashcards.length) % flashcards.length);
  };

  const handleGenerate = async () => {
    if (!selectedNoteId) {
      toast.error('Please select a note to generate flashcards from');
      return;
    }

    setIsGenerating(true);
    // Show loading notification
    const loadingToast = toast.loading('Please wait a moment, generating flashcards...');
    try {
      const response = await flashcardService.generateFromNote(selectedNoteId, 5);
      const newFlashcards = response.data.flashcards || [];
      setFlashcards([...newFlashcards, ...flashcards]);
      setSelectedNoteId('');
      setIsGenerating(false);
      setShowGenerator(false);
      // Reset to show the first new flashcard
      setCurrentCard(0);
      setIsFlipped(false);
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`${newFlashcards.length} flashcards generated successfully!`);
      fetchStats(); // Refresh stats
      // Scroll to the flashcard viewer
      setTimeout(() => {
        const flashcardViewer = document.querySelector('.flashcard-page main');
        if (flashcardViewer) {
          flashcardViewer.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to generate flashcards';
      // Dismiss loading toast and show error
      toast.dismiss(loadingToast);
      toast.error(message);
      console.error('Generate error:', error);
      setIsGenerating(false);
    }
  };

  const toggleMastery = async (id) => {
    try {
      const card = flashcards.find(c => c._id === id);
      const newMasteryState = !card.mastery || card.mastery < 90;
      
      await flashcardService.toggleMastery(id, newMasteryState);
      
      // Update local state
      setFlashcards(flashcards.map(card =>
        card._id === id 
          ? { 
              ...card, 
              mastery: newMasteryState ? 100 : 0,
              lastReviewed: new Date().toISOString()
            } 
          : card
      ));
      
      fetchStats(); // Refresh stats
      toast.success(`Card marked as ${newMasteryState ? 'mastered' : 'needs review'}`);
    } catch (error) {
      toast.error('Failed to update mastery');
      console.error('Toggle mastery error:', error);
    }
  };

  const handleReview = async (isCorrect) => {
    if (!flashcards[currentCard]) return;
    
    try {
      const cardId = flashcards[currentCard]._id;
      await flashcardService.reviewFlashcard(cardId, isCorrect);
      
      // Update local state
      setFlashcards(flashcards.map(card => {
        if (card._id === cardId) {
          const newMastery = isCorrect ? 
            Math.min(card.mastery + 20, 100) : 
            Math.max(card.mastery - 10, 0);
          
          return {
            ...card,
            mastery: newMastery,
            lastReviewed: new Date().toISOString(),
            nextReview: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // Next day
          };
        }
        return card;
      }));
      
      toast.success(`Reviewed as ${isCorrect ? 'correct' : 'incorrect'}`);
      handleNext(); // Move to next card
    } catch (error) {
      toast.error('Failed to save review');
      console.error('Review error:', error);
    }
  };

  const handleDeleteCard = async (id) => {
    if (!window.confirm('Are you sure you want to delete this flashcard?')) return;
    
    try {
      await flashcardService.deleteFlashcard(id);
      setFlashcards(flashcards.filter(card => card._id !== id));
      
      // Adjust current card if needed
      if (currentCard >= flashcards.length - 1 && flashcards.length > 1) {
        setCurrentCard(flashcards.length - 2);
      }
      
      toast.success('Flashcard deleted successfully!');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete flashcard');
      console.error('Delete error:', error);
    }
  };

  const handleCreateFlashcard = async () => {
    try {
      const newCard = {
        question: 'New flashcard question',
        answer: 'New flashcard answer',
        tags: ['custom'],
        difficulty: 'medium'
      };
      
      const response = await flashcardService.createFlashcard(newCard);
      setFlashcards([response.data.flashcard, ...flashcards]);
      setCurrentCard(0); // Show the new card
      toast.success('New flashcard created!');
      fetchStats();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create flashcard';
      toast.error(message);
      console.error('Create error:', error);
    }
  };

  const isMastered = (card) => {
    return card.mastery >= 90;
  };

  return (
    <div className={`flashcard-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 w-full overflow-x-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Flashcards</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2">AI-generated flashcards for effective revision</p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-300">Loading flashcards...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Flashcard Viewer */}
                <div className="lg:col-span-2">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-8">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Card {currentCard + 1} of {flashcards.length}
                        </span>
                        {flashcards[currentCard]?.tags && (
                          <div className="mt-2 flex gap-2">
                            {flashcards[currentCard].tags.map(tag => (
                              <span key={tag} className="px-3 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleCreateFlashcard}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition"
                      >
                        + New Card
                      </button>
                    </div>

                    {/* Flashcard */}
                    {flashcards.length > 0 ? (
                      <>
                        <div
                          className={`relative h-64 w-full cursor-pointer transition-all duration-500 ${
                            isFlipped ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-gray-50 dark:bg-gray-700'
                          } rounded-lg border-2 ${
                            isMastered(flashcards[currentCard])
                              ? 'border-green-500 dark:border-green-400'
                              : 'border-gray-200 dark:border-gray-600'
                          }`}
                          onClick={() => setIsFlipped(!isFlipped)}
                        >
                          <div className="absolute inset-0 flex items-center justify-center p-6">
                            <p className="text-xl font-medium text-center text-gray-900 dark:text-white">
                              {isFlipped 
                                ? flashcards[currentCard]?.answer 
                                : flashcards[currentCard]?.question
                              }
                            </p>
                          </div>
                          <div className="absolute top-4 right-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMastery(flashcards[currentCard]._id);
                              }}
                              className={`p-2 rounded-full ${
                                isMastered(flashcards[currentCard])
                                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                              }`}
                            >
                              {isMastered(flashcards[currentCard]) ? '✅ Mastered' : '📚 Learn'}
                            </button>
                          </div>
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-sm text-gray-500 dark:text-gray-400">
                            Click to flip
                          </div>
                        </div>

                        {/* Navigation and Review Buttons */}
                        <div className="flex justify-center gap-4 mt-8">
                          <button
                            onClick={handlePrev}
                            className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                          >
                            ← Previous
                          </button>
                          <button
                            onClick={() => setIsFlipped(!isFlipped)}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                          >
                            {isFlipped ? 'Show Question' : 'Show Answer'}
                          </button>
                          <button
                            onClick={handleNext}
                            className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                          >
                            Next →
                          </button>
                        </div>

                        {/* Review Buttons */}
                        <div className="flex justify-center gap-4 mt-6">
                          <button
                            onClick={() => handleReview(false)}
                            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                          >
                            ❌ Needs Review
                          </button>
                          <button
                            onClick={() => handleReview(true)}
                            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                          >
                            ✅ Correct
                          </button>
                        </div>

                        {/* Progress */}
                        <div className="mt-8">
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span>Mastery Progress</span>
                            <span>{stats.masteredCards || 0}/{stats.totalCards || 0}</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${stats.masteryPercentage || 0}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400">No flashcards yet. Create your first one!</p>
                        <button
                          onClick={handleCreateFlashcard}
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition"
                        >
                          Create First Flashcard
                        </button>
                      </div>
                    )}
                  </div>

                  {/* All Flashcards List */}
                  {flashcards.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        All Flashcards ({flashcards.length})
                      </h3>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {flashcards.map((card, index) => (
                          <div
                            key={card._id}
                            className={`p-4 rounded-lg border cursor-pointer transition ${
                              currentCard === index
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                            } ${isMastered(card) ? 'bg-green-50 dark:bg-green-900/10' : ''}`}
                            onClick={() => {
                              setCurrentCard(index);
                              setIsFlipped(false);
                            }}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <h4 className="font-bold text-gray-900 dark:text-white">{card.question}</h4>
                                <div className="flex gap-2 mt-2">
                                  {card.tags && card.tags.map(tag => (
                                    <span key={tag} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                                      {tag}
                                    </span>
                                  ))}
                                  {card.difficulty && (
                                    <span className={`px-2 py-1 text-xs rounded ${
                                      card.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' :
                                      card.difficulty === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300' :
                                      'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                                    }`}>
                                      {card.difficulty}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {isMastered(card) && (
                                  <span className="text-green-600 dark:text-green-400 text-sm">✅ Mastered</span>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMastery(card._id);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                                    title={isMastered(card) ? 'Mark as needs review' : 'Mark as mastered'}
                                  >
                                    {isMastered(card) ? '🔄 Reset' : '✅ Master'}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCard(card._id);
                                    }}
                                    className="text-red-400 hover:text-red-600 p-1"
                                    title="Delete flashcard"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Generator & Stats */}
                <div>
                  {/* Stats */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Flashcard Stats</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Total Cards</span>
                        <span className="font-bold text-gray-900 dark:text-white">{stats.totalCards || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Mastered</span>
                        <span className="font-bold text-green-600 dark:text-green-400">
                          {stats.masteredCards || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Due for Review</span>
                        <span className="font-bold text-yellow-600 dark:text-yellow-400">
                          {stats.dueForReview || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Mastery Rate</span>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {stats.masteryPercentage || 0}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Generator */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Generate Flashcards</h3>
                      <button
                        onClick={() => setShowGenerator(!showGenerator)}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {showGenerator ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {showGenerator && (
                      <>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Select a note to generate flashcards from
                        </p>
                        {clusters.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">Or select a cluster:</label>
                            <select
                              value={selectedCluster}
                              onChange={(e) => setSelectedCluster(e.target.value)}
                              className="w-full p-2 border rounded mb-2"
                            >
                              <option value="all">All clusters</option>
                              {clusters.map(c => (
                                <option key={c._id} value={c._id}>{c._id} ({c.count})</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500">Clusters allow organizing notes by topic for targeted flashcard generation.</p>
                          </div>
                        )}
                        {notes.length > 0 ? (
                          <select
                            value={selectedNoteId}
                            onChange={(e) => setSelectedNoteId(e.target.value)}
                            className="w-full p-4 mb-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Choose a note...</option>
                            {notes.map(note => (
                              <option key={note._id} value={note._id}>
                                {note.title || 'Untitled Note'}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="w-full p-4 mb-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-center">
                            No notes available. Create some notes first!
                          </div>
                        )}
                        <button
                          onClick={handleGenerate}
                          disabled={!selectedNoteId || isGenerating}
                          className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-bold rounded-lg hover:opacity-90 transition disabled:opacity-50"
                        >
                          {isGenerating ? 'Generating...' : 'Generate Flashcards'}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Exam Questions Generator */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Generate Exam Questions</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Create practice exam questions from your flashcards
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const noteId = flashcards.length > 0 && flashcards[0].note ? flashcards[0].note._id : null;
                          if (!noteId) {
                            toast.warning('No note associated with flashcards to generate questions from');
                            return;
                          }
                          const response = await flashcardService.generateExamQuestions(noteId, 5);
                          const questions = response.data.questions || [];
                          if (questions.length > 0) {
                            // Generate PDF with exam questions
                            const doc = new jsPDF();
                            let yPosition = 10;
                            const pageHeight = doc.internal.pageSize.getHeight();
                            const margin = 10;
                            const maxWidth = doc.internal.pageSize.getWidth() - 2 * margin;
                            
                            // Add title
                            doc.setFontSize(18);
                            doc.setFont(undefined, 'bold');
                            doc.text('Practice Exam Questions', margin, yPosition);
                            yPosition += 10;
                            
                            // Add date
                            doc.setFontSize(10);
                            doc.setTextColor(128, 128, 128);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPosition);
                            yPosition += 8;
                            
                            // Add separator line
                            doc.setDrawColor(200, 200, 200);
                            doc.line(margin, yPosition, doc.internal.pageSize.getWidth() - margin, yPosition);
                            yPosition += 10;
                            
                            // Add questions
                            questions.forEach((question, index) => {
                              // Check if we need a new page
                              if (yPosition > pageHeight - 30) {
                                doc.addPage();
                                yPosition = 10;
                              }
                              
                              // Question number and text
                              doc.setFontSize(11);
                              doc.setTextColor(0, 0, 0);
                              doc.setFont(undefined, 'bold');
                              
                              const questionText = `Q${index + 1}. ${question.question || question.text || 'Question'}`;
                              const questionLines = doc.splitTextToSize(questionText, maxWidth - 4);
                              
                              questionLines.forEach((line, lineIndex) => {
                                if (yPosition > pageHeight - 30) {
                                  doc.addPage();
                                  yPosition = 10;
                                }
                                if (lineIndex === 0) {
                                  doc.text(line, margin + 2, yPosition);
                                } else {
                                  doc.text(line, margin + 4, yPosition);
                                }
                                yPosition += 6;
                              });
                              
                              yPosition += 2;
                              
                              // Answer options
                              doc.setFontSize(10);
                              doc.setFont(undefined, 'normal');
                              
                              if (question.options && Array.isArray(question.options)) {
                                question.options.forEach((option, optIndex) => {
                                  if (yPosition > pageHeight - 20) {
                                    doc.addPage();
                                    yPosition = 10;
                                  }
                                  const optionLetter = String.fromCharCode(65 + optIndex); // A, B, C, D
                                  const optionText = `${optionLetter}. ${option}`;
                                  const optionLines = doc.splitTextToSize(optionText, maxWidth - 8);
                                  
                                  optionLines.forEach((line, lineIndex) => {
                                    if (yPosition > pageHeight - 20) {
                                      doc.addPage();
                                      yPosition = 10;
                                    }
                                    doc.text(line, margin + 4, yPosition);
                                    yPosition += 5;
                                  });
                                });
                              }
                              
                              // Add spacing between questions
                              yPosition += 5;
                              if (index < questions.length - 1) {
                                doc.setDrawColor(220, 220, 220);
                                doc.line(margin, yPosition, doc.internal.pageSize.getWidth() - margin, yPosition);
                                yPosition += 6;
                              }
                            });
                            
                            // Save the PDF
                            doc.save(`exam_questions_${new Date().toISOString().split('T')[0]}.pdf`);
                            toast.success(`Generated and downloaded ${questions.length} exam questions as PDF!`);
                          } else {
                            toast.warning('No questions generated');
                          }
                        } catch (error) {
                          toast.error('Failed to generate exam questions');
                          console.error('Exam generation error:', error);
                        }
                      }}
                      className="w-full py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold rounded-lg hover:opacity-90 transition"
                    >
                      Generate & Download Exam Questions as PDF
                    </button>
                  </div>

                  {/* PDF Export */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Export to PDF</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Download your flashcards as a PDF for offline study
                    </p>
                    <div className="mb-3">
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">Cluster</label>
                      <select value={exportCluster} onChange={(e) => setExportCluster(e.target.value)} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white">
                        <option value="all">All Flashcards</option>
                        {availableClusters.map(cluster => (
                          <option key={cluster} value={cluster}>{cluster}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Export selected cluster (or all) to printable HTML and trigger print
                          const cards = exportCluster === 'all' ? flashcards : flashcards.filter(c => c.note?.cluster === exportCluster);
                          if (!cards || cards.length === 0) {
                            toast.warning('No flashcards found for selected cluster');
                            return;
                          }
                          const html = `<!doctype html><html><head><meta charset="utf-8"><title>Flashcards${exportCluster==='all' ? '' : ' - '+exportCluster}</title><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial; padding:24px; color:#111827} h1{font-size:20px;margin-bottom:8px} .qa{margin-bottom:18px} .q{font-weight:600} .a{margin-top:6px;color:#374151}</style></head><body><h1>Flashcards ${exportCluster==='all' ? '' : ' - '+exportCluster}</h1>${cards.map((c,i)=>`<div class="qa"><div class="q">Q${i+1}: ${c.question}</div><div class="a">A: ${c.answer}</div></div>`).join('')}</body></html>`;
                          const win = window.open('', '_blank');
                          if (!win) { return alert('Popup blocked. Allow popups to export PDF.'); }
                          win.document.open(); win.document.write(html); win.document.close();
                          setTimeout(()=>{ try { win.focus(); win.print(); } catch(e){ console.error(e); } }, 500);
                        }}
                        className="flex-1 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold rounded-lg hover:opacity-90 transition"
                      >
                        Export Topic PDF
                      </button>

                      <button
                        onClick={async () => {
                          // Generate exam PDF from all flashcards (Q/A per page)
                          if (!flashcards || flashcards.length === 0) { toast.warning('No flashcards available'); return; }
                          const cards = flashcards;
                          const html = `<!doctype html><html><head><meta charset="utf-8"><title>Exam Questions</title><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial; padding:24px; color:#111827} h1{font-size:20px;margin-bottom:12px} .qa{page-break-after:always;margin-bottom:24px} .q{font-weight:700;font-size:18px} .a{margin-top:12px;color:#374151}</style></head><body><h1>Exam Questions</h1>${cards.map((c,i)=>`<div class="qa"><div class="q">Q${i+1}: ${c.question}</div><div class="a">A: ${c.answer}</div></div>`).join('')}</body></html>`;
                          const win = window.open('', '_blank');
                          if (!win) { return alert('Popup blocked. Allow popups to export PDF.'); }
                          win.document.open(); win.document.write(html); win.document.close();
                          setTimeout(()=>{ try { win.focus(); win.print(); } catch(e){ console.error(e); } }, 500);
                        }}
                        className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 transition"
                      >
                        Generate Exam PDF
                      </button>
                    </div>
                  </div>

                  {/* Study Modes */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Study Modes</h3>
                    <div className="space-y-3">
                      <button 
                        onClick={() => {
                          // Filter cards that need review
                          const dueCards = flashcards.filter(card => 
                            !card.nextReview || new Date(card.nextReview) <= new Date()
                          );
                          if (dueCards.length > 0) {
                            setFlashcards(dueCards);
                            setCurrentCard(0);
                            toast.success(`Starting review of ${dueCards.length} cards`);
                          } else {
                            toast.info('No cards due for review!');
                          }
                        }}
                        className="w-full p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                      >
                        <div className="font-bold text-gray-900 dark:text-white">Due for Review</div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Review cards that are due ({stats.dueForReview || 0} cards)
                        </p>
                      </button>
                      <button 
                        onClick={() => {
                          const unmastered = flashcards.filter(card => !isMastered(card));
                          if (unmastered.length > 0) {
                            setFlashcards(unmastered);
                            setCurrentCard(0);
                            toast.success(`Focusing on ${unmastered.length} unmastered cards`);
                          } else {
                            toast.info('All cards are mastered!');
                          }
                        }}
                        className="w-full p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                      >
                        <div className="font-bold text-gray-900 dark:text-white">Mastery Mode</div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Focus on unmastered cards
                        </p>
                      </button>
                      <button className="w-full p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                        <div className="font-bold text-gray-900 dark:text-white">Random Order</div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Shuffle all cards for variety
                        </p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default FlashcardPage;